/**
 * File Upload Utility — delegates to network layer.
 */

import { dataService } from '@/api';
import { UPLOAD_MAX_ATTEMPTS } from './uploadTimeout';

export { getUploadTimeoutMs } from './uploadTimeout';

export function formatFileSize(bytes) {
  const size = Number(bytes) || 0;
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${size} B`;
}

function extOf(name) {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : '';
}

function inferMimeFromName(name) {
  const ext = extOf(name);
  if (!ext) return '';
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'mp4') return 'video/mp4';
  if (ext === 'mov') return 'video/quicktime';
  if (ext === 'm4v') return 'video/x-m4v';
  if (ext === '3gp') return 'video/3gpp';
  if (ext === '3g2') return 'video/3gpp2';
  if (ext === 'webm') return 'video/webm';
  if (ext === 'ogg') return 'video/ogg';
  if (ext === 'mp3') return 'audio/mpeg';
  if (ext === 'wav') return 'audio/wav';
  return '';
}

export async function uploadFile(file, options = {}) {
  return dataService.uploadFile(file, {
    maxAttempts: UPLOAD_MAX_ATTEMPTS,
    ...options,
  });
}

export function validateFile(file, options) {
  const { maxSize = 10 * 1024 * 1024, allowedTypes = [] } = options;
  const normalizedType = (file.type || '').split(';')[0].trim();
  const inferredType = !normalizedType ? inferMimeFromName(file.name) : '';
  if (typeof maxSize === 'number' && maxSize > 0 && file.size > maxSize) {
    return {
      valid: false,
      error: `File size exceeds ${maxSize / (1024 * 1024)}MB limit`,
    };
  }
  if (
    allowedTypes.length > 0 &&
    !allowedTypes.includes(normalizedType) &&
    !allowedTypes.includes(file.type) &&
    (!inferredType || !allowedTypes.includes(inferredType))
  ) {
    return {
      valid: false,
      error: `File type ${file.type || '(unknown)'} is not allowed. Allowed types: ${allowedTypes.join(', ')}`,
    };
  }
  return { valid: true };
}

export const FILE_TYPE_PRESETS = {
  image: {
    allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jpg'],
    maxSize: 0,
  },
  pdf: {
    allowedTypes: ['application/pdf', 'application/x-pdf'],
    maxSize: 50 * 1024 * 1024,
  },
  video: {
    allowedTypes: [],
    maxSize: 500 * 1024 * 1024,
  },
  audio: {
    allowedTypes: [],
    maxSize: 50 * 1024 * 1024,
  },
};
